import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import DateInput from '../ui/date-input';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';

const WEEKDAYS = [
  { key: 'mon', label: 'Mon' },
  { key: 'tue', label: 'Tue' },
  { key: 'wed', label: 'Wed' },
  { key: 'thu', label: 'Thu' },
  { key: 'fri', label: 'Fri' },
  { key: 'sat', label: 'Sat' },
  { key: 'sun', label: 'Sun' },
];

export default function CrmAttendanceSettings() {
  const { user } = useAuth();
  const { toast } = useToast();
  const isSuperAdmin = user?.role_slug === 'super_admin';
  const [weekendDays, setWeekendDays] = useState(['sun']);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [holidayForm, setHolidayForm] = useState({ date: '', name: '' });

  const fetchSettings = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoading(true);
    try {
      const res = await api.get('/crm/attendance-settings');
      const data = res.data?.data || {};
      setWeekendDays(data.weekend_days || ['sun']);
      setHolidays(data.holidays || []);
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, toast]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  if (!isSuperAdmin) return null;

  const toggleWeekday = (key) => {
    setWeekendDays((prev) => (
      prev.includes(key) ? prev.filter((d) => d !== key) : [...prev, key]
    ));
  };

  const saveWeekends = async () => {
    setSaving(true);
    try {
      await api.put('/crm/attendance-settings/weekends', { weekend_days: weekendDays });
      toast({ title: 'Saved', description: 'Week-off days updated.' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const addHoliday = async () => {
    if (!holidayForm.date || !holidayForm.name.trim()) {
      toast({ title: 'Required', description: 'Date and name are required.', variant: 'destructive' });
      return;
    }
    try {
      await api.post('/crm/attendance-settings/holidays', holidayForm);
      setHolidayForm({ date: '', name: '' });
      fetchSettings();
      toast({ title: 'Added', description: 'Holiday added.' });
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  const removeHoliday = async (id) => {
    try {
      await api.delete(`/crm/attendance-settings/holidays/${id}`);
      fetchSettings();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.message, variant: 'destructive' });
    }
  };

  return (
    <Card className="border-border">
      <CardHeader>
        <CardTitle className="text-base">CRM Attendance Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading settings…</p>
        ) : (
          <>
            <div>
              <Label className="text-sm font-medium">Week-off days</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {WEEKDAYS.map((day) => (
                  <Button
                    key={day.key}
                    type="button"
                    size="sm"
                    variant={weekendDays.includes(day.key) ? 'default' : 'outline'}
                    onClick={() => toggleWeekday(day.key)}
                  >
                    {day.label}
                  </Button>
                ))}
              </div>
              <Button className="mt-3" size="sm" onClick={saveWeekends} disabled={saving}>
                {saving ? 'Saving…' : 'Save week-offs'}
              </Button>
            </div>

            <div>
              <Label className="text-sm font-medium">Holidays</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                <DateInput
                  value={holidayForm.date}
                  onChange={(v) => setHolidayForm((f) => ({ ...f, date: v }))}
                />
                <Input
                  placeholder="Holiday name"
                  value={holidayForm.name}
                  onChange={(e) => setHolidayForm((f) => ({ ...f, name: e.target.value }))}
                />
                <Button type="button" onClick={addHoliday}>Add holiday</Button>
              </div>
              {holidays.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-3">No holidays configured.</p>
              ) : (
                <ul className="mt-3 space-y-2">
                  {holidays.map((h) => (
                    <li key={h.id} className="flex items-center justify-between text-sm border border-border rounded-md px-3 py-2">
                      <span>{h.date} — {h.name}</span>
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeHoliday(h.id)}>Remove</Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
